import { OrganizationPerms } from '@/organization/useOrganizationRole';
import { MoreVert } from '@mui/icons-material';
import { Box, Menu, MenuItem, Typography } from '@mui/material';

interface ProjectCardMenusProps {
  // Project Menu
  projectMenuAnchor: HTMLElement | null;
  onProjectMenuClose: (e: any) => void;
  allSubprojectsCompleted: boolean;
  isArchived: boolean;
  perms?: OrganizationPerms;
  onBOApproval: () => void;
  onPencilsDown: () => void;
  onDuplicate: () => void;
  onArchive: () => void;
  onRestore: () => void;
  onDeleteArchived: () => void;
  onEdit: () => void;
  onDelete: () => void;

  // Subproject Menu
  subprojectMenuAnchor: { anchor: HTMLElement; subprojectId: string } | null;
  onSubprojectMenuClose: (e: any) => void;
  mainStatus: string;
  onEditSubproject: (subprojectId: string) => void;
  onViewSubproject: (subprojectId: string) => void;
  onDuplicateSubproject: (subprojectId: string) => void;
  onDeleteSubproject: (subprojectId: string) => void;
}

export function ProjectCardMenus({
  projectMenuAnchor,
  onProjectMenuClose,
  allSubprojectsCompleted,
  isArchived,
  perms,
  onBOApproval,
  onPencilsDown,
  onDuplicate,
  onArchive,
  onRestore,
  onDeleteArchived,
  onEdit,
  onDelete,
  subprojectMenuAnchor,
  onSubprojectMenuClose,
  mainStatus,
  onEditSubproject,
  onViewSubproject,
  onDuplicateSubproject,
  onDeleteSubproject,
}: ProjectCardMenusProps) {
  return (
    <>
      {/* Project Menu */}
      <Menu
        anchorEl={projectMenuAnchor}
        open={Boolean(projectMenuAnchor)}
        onClose={onProjectMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            borderRadius: 0,
            overflow: 'hidden',
            minWidth: 'clamp(120px, 9vw, 170px)',
            border: 'none',
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        {/* Menu Header with More Button Icon */}
        <Box
          onClick={onProjectMenuClose}
          sx={{
            backgroundColor: 'rgba(120, 155, 151, 1)',
            px: 'clamp(10px, 0.8vw, 14px)',
            py: 'clamp(4px, 0.4vh, 6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            minHeight: 'clamp(24px, 2.2vh, 32px)',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(110, 145, 141, 1)',
            },
          }}
        >
          <MoreVert
            sx={{ fontSize: 'clamp(14px, 1.1vw, 18px)', color: '#000000' }}
          />
        </Box>

        {/* BO Approval - only visible for Budget Manager and all subprojects completed */}
        {perms?.canApproveBudget && allSubprojectsCompleted && !isArchived && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              onProjectMenuClose(e);
              onBOApproval();
            }}
            sx={{
              backgroundColor: 'rgba(195, 210, 195, 1)',
              py: 'clamp(4px, 0.4vh, 6px)',
              px: 'clamp(10px, 0.8vw, 14px)',
              minHeight: 'clamp(24px, 2.2vh, 32px)',
              '&:hover': {
                backgroundColor: 'rgba(175, 190, 175, 1)',
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 'clamp(10px, 0.75vw, 12px)',
                fontWeight: 500,
                color: '#000000',
              }}
            >
              BO Approval
            </Typography>
          </MenuItem>
        )}

        {/* Pencils Down - only visible for Program Manager and all subprojects completed */}
        {perms?.canApproveDesign && allSubprojectsCompleted && !isArchived && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              onProjectMenuClose(e);
              onPencilsDown();
            }}
            sx={{
              backgroundColor: 'rgba(195, 210, 195, 1)',
              py: 'clamp(4px, 0.4vh, 6px)',
              px: 'clamp(10px, 0.8vw, 14px)',
              minHeight: 'clamp(24px, 2.2vh, 32px)',
              '&:hover': {
                backgroundColor: 'rgba(175, 190, 175, 1)',
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 'clamp(10px, 0.75vw, 12px)',
                fontWeight: 500,
                color: '#000000',
              }}
            >
              Pencils Down
            </Typography>
          </MenuItem>
        )}

        {/* Duplicate */}
        {!isArchived && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              onProjectMenuClose(e);
              onDuplicate();
            }}
            sx={{
              backgroundColor: 'rgba(195, 210, 195, 1)',
              py: 'clamp(4px, 0.4vh, 6px)',
              px: 'clamp(10px, 0.8vw, 14px)',
              minHeight: 'clamp(24px, 2.2vh, 32px)',
              '&:hover': {
                backgroundColor: 'rgba(175, 190, 175, 1)',
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 'clamp(10px, 0.75vw, 12px)',
                fontWeight: 500,
                color: '#000000',
              }}
            >
              Duplicate
            </Typography>
          </MenuItem>
        )}

        {/* Archive/Unarchive */}
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            onProjectMenuClose(e);
            if (isArchived) {
              onRestore();
            } else {
              onArchive();
            }
          }}
          sx={{
            backgroundColor: 'rgba(195, 210, 195, 1)',
            py: 'clamp(4px, 0.4vh, 6px)',
            px: 'clamp(10px, 0.8vw, 14px)',
            minHeight: 'clamp(24px, 2.2vh, 32px)',
            '&:hover': {
              backgroundColor: 'rgba(175, 190, 175, 1)',
            },
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 'clamp(10px, 0.75vw, 12px)',
              fontWeight: 500,
              color: '#000000',
            }}
          >
            {isArchived ? 'Unarchive' : 'Archive'}
          </Typography>
        </MenuItem>

        {/* Delete Project - only for archived projects */}
        {isArchived && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              onProjectMenuClose(e);
              onDeleteArchived();
            }}
            sx={{
              backgroundColor: 'rgba(195, 210, 195, 1)',
              py: 'clamp(4px, 0.4vh, 6px)',
              px: 'clamp(10px, 0.8vw, 14px)',
              minHeight: 'clamp(24px, 2.2vh, 32px)',
              '&:hover': {
                backgroundColor: 'rgba(175, 190, 175, 1)',
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 'clamp(10px, 0.75vw, 12px)',
                fontWeight: 500,
                color: '#000000',
              }}
            >
              Delete Project
            </Typography>
          </MenuItem>
        )}

        {/* Edit Project */}
        {!isArchived && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              onProjectMenuClose(e);
              onEdit();
            }}
            sx={{
              backgroundColor: 'rgba(195, 210, 195, 1)',
              py: 'clamp(4px, 0.4vh, 6px)',
              px: 'clamp(10px, 0.8vw, 14px)',
              minHeight: 'clamp(24px, 2.2vh, 32px)',
              '&:hover': {
                backgroundColor: 'rgba(175, 190, 175, 1)',
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 'clamp(10px, 0.75vw, 12px)',
                fontWeight: 500,
                color: '#000000',
              }}
            >
              Edit Project
            </Typography>
          </MenuItem>
        )}

        {/* Delete Project - only visible for Organization Admin and non-archived projects */}
        {perms?.canEditOrgSettings && !isArchived && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              onProjectMenuClose(e);
              onDelete();
            }}
            sx={{
              backgroundColor: 'rgba(195, 210, 195, 1)',
              py: 'clamp(4px, 0.4vh, 6px)',
              px: 'clamp(10px, 0.8vw, 14px)',
              minHeight: 'clamp(24px, 2.2vh, 32px)',
              '&:hover': {
                backgroundColor: 'rgba(175, 190, 175, 1)',
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 'clamp(10px, 0.75vw, 12px)',
                fontWeight: 500,
                color: '#000000',
              }}
            >
              Delete Project
            </Typography>
          </MenuItem>
        )}
      </Menu>

      {/* Subproject Menu */}
      <Menu
        anchorEl={subprojectMenuAnchor?.anchor}
        open={Boolean(subprojectMenuAnchor)}
        onClose={onSubprojectMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            borderRadius: 0,
            overflow: 'hidden',
            minWidth: 'clamp(120px, 9vw, 170px)',
            border: 'none',
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        {/* Menu Header with More Button Icon */}
        <Box
          onClick={onSubprojectMenuClose}
          sx={{
            backgroundColor: 'rgba(120, 155, 151, 1)',
            px: 'clamp(10px, 0.8vw, 14px)',
            py: 'clamp(4px, 0.4vh, 6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            minHeight: 'clamp(24px, 2.2vh, 32px)',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(110, 145, 141, 1)',
            },
          }}
        >
          <MoreVert
            sx={{ fontSize: 'clamp(14px, 1.1vw, 18px)', color: '#000000' }}
          />
        </Box>

        {/* Edit Subproject - Open edit modal */}
        {!isArchived && mainStatus !== 'Pencils Down' && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              if (subprojectMenuAnchor) {
                onEditSubproject(subprojectMenuAnchor.subprojectId);
              }
              onSubprojectMenuClose(e);
            }}
            sx={{
              backgroundColor: 'rgba(195, 210, 195, 1)',
              py: 'clamp(4px, 0.4vh, 6px)',
              px: 'clamp(10px, 0.8vw, 14px)',
              minHeight: 'clamp(24px, 2.2vh, 32px)',
              '&:hover': {
                backgroundColor: 'rgba(175, 190, 175, 1)',
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 'clamp(10px, 0.75vw, 12px)',
                fontWeight: 500,
                color: '#000000',
              }}
            >
              Edit Subproject
            </Typography>
          </MenuItem>
        )}

        {/* View Subproject - For archived/pencils down */}
        {(isArchived || mainStatus === 'Pencils Down') && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              if (subprojectMenuAnchor) {
                onViewSubproject(subprojectMenuAnchor.subprojectId);
              }
              onSubprojectMenuClose(e);
            }}
            sx={{
              backgroundColor: 'rgba(195, 210, 195, 1)',
              py: 'clamp(4px, 0.4vh, 6px)',
              px: 'clamp(10px, 0.8vw, 14px)',
              minHeight: 'clamp(24px, 2.2vh, 32px)',
              '&:hover': {
                backgroundColor: 'rgba(175, 190, 175, 1)',
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 'clamp(10px, 0.75vw, 12px)',
                fontWeight: 500,
                color: '#000000',
              }}
            >
              View Subproject
            </Typography>
          </MenuItem>
        )}

        {/* Duplicate Subproject */}
        {!isArchived && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              if (subprojectMenuAnchor) {
                onDuplicateSubproject(subprojectMenuAnchor.subprojectId);
              }
              onSubprojectMenuClose(e);
            }}
            sx={{
              backgroundColor: 'rgba(195, 210, 195, 1)',
              py: 'clamp(4px, 0.4vh, 6px)',
              px: 'clamp(10px, 0.8vw, 14px)',
              minHeight: 'clamp(24px, 2.2vh, 32px)',
              '&:hover': {
                backgroundColor: 'rgba(175, 190, 175, 1)',
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 'clamp(10px, 0.75vw, 12px)',
                fontWeight: 500,
                color: '#000000',
              }}
            >
              Duplicate Subproject
            </Typography>
          </MenuItem>
        )}

        {/* Delete Subproject - only visible for Organization Admin */}
        {perms?.canEditOrgSettings && !isArchived && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              if (subprojectMenuAnchor) {
                onDeleteSubproject(subprojectMenuAnchor.subprojectId);
              }
              onSubprojectMenuClose(e);
            }}
            sx={{
              backgroundColor: 'rgba(195, 210, 195, 1)',
              py: 'clamp(4px, 0.4vh, 6px)',
              px: 'clamp(10px, 0.8vw, 14px)',
              minHeight: 'clamp(24px, 2.2vh, 32px)',
              '&:hover': {
                backgroundColor: 'rgba(175, 190, 175, 1)',
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 'clamp(10px, 0.75vw, 12px)',
                fontWeight: 500,
                color: '#000000',
              }}
            >
              Delete Subproject
            </Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
